/*
 * ai-label/inspect.js — what a file SAYS about where it came from (/ai-label, «Проверить»).
 *
 * Reads the places where generators, cameras, editors and labelling tools write their claims —
 * and nothing else: no pixel statistics, no «probability of AI». A file that says nothing is a
 * file that says nothing (metadata is stripped by every messenger and most platforms), and the
 * page must be able to tell that apart from «this is not AI».
 *
 *   AiLabelInspect.inspect(blob, name) → Promise<report>
 *   AiLabelInspect.verdict(report)      → {level, reasons[], contradictions[]}   (pure)
 *
 * report = {
 *   format: 'jpeg'|'png'|'webp'|'heif'|'avif'|'gif'|'mp4'|'mov'|'m4a'|'webm'|'mp3'|'wav'|'ogg'|'flac'|'unknown',
 *   size, c2pa: {present, where}, xmp: [{where, props}], exif: {...}, texts: [{where, key, value}],
 *   items: [{kind, source, key, value, code}]   — normalised claims, one per thing found
 * }
 * item.kind: 'ai' (says fully generated) | 'ai_partial' (generated parts / generative edit) |
 *            'ai_maybe' (a hint: an AI app's name, «possibly AI») | 'enhanced' (algorithmic
 *            enhancement, not generation) | 'capture' (says a camera made it) | 'info'.
 * item.code: a stable id the page explains in the reader's language (ai_label.js).
 *
 * Every walker is bounded: images are read whole (they are bounded by what a canvas can hold);
 * audio and video by their headers and at most a few MB of head and tail.
 */
(function (root) {
    'use strict';

    var DST_URI = 'http://cv.iptc.org/newscodes/digitalsourcetype/';
    var NS = {
        iptcExt: 'http://iptc.org/std/Iptc4xmpExt/2008-02-29/',
        tc260: 'http://www.tc260.org.cn/ns/AIGC/1.0/',
        tmbai: 'https://timbrica.com/ns/ai-label/1.0/',
        xmp: 'http://ns.adobe.com/xap/1.0/',
        dc: 'http://purl.org/dc/elements/1.1/',
        photoshop: 'http://ns.adobe.com/photoshop/1.0/',
        tiff: 'http://ns.adobe.com/tiff/1.0/',
        exif: 'http://ns.adobe.com/exif/1.0/',
        plus: 'http://ns.useplus.org/ldf/xmp/1.0/',
        xmpMM: 'http://ns.adobe.com/xap/1.0/mm/',
        dcterms: 'http://purl.org/dc/terms/'
    };
    var C2PA_UUID = 'd8fec3d61b0e483c92975828877ec481';
    var XMP_UUID = 'be7acfcb97a942e89c71999491e3afac';
    var OUR_NOTE = 'AI-generated content (tool:';

    // DigitalSourceType → kind. The IPTC NewsCodes, current and retired.
    var DST_KIND = {
        trainedAlgorithmicMedia: 'ai',
        algorithmicMedia: 'info',
        compositeWithTrainedAlgorithmicMedia: 'ai_partial',
        compositeSynthetic: 'ai_partial',
        algorithmicallyEnhanced: 'enhanced',
        digitalCapture: 'capture',
        negativeFilm: 'capture', positiveFilm: 'capture', print: 'capture',
        minorHumanEdits: 'capture', humanEdits: 'info', compositeCapture: 'capture',
        dataDrivenMedia: 'info', digitalCreation: 'info', digitalArt: 'info', virtualRecording: 'info',
        screenCapture: 'info', composite: 'info'
    };

    // Names an AI generator puts in Software / CreatorTool / encoder fields — a HINT, never a proof:
    // «Adobe Photoshop» is not on the list (it edits real photos); its generative edits say so
    // in DigitalSourceType and C2PA, which are read separately.
    var AI_APPS = [
        [/midjourney/i, 'Midjourney'], [/dall[\s·.-]?e/i, 'DALL·E'], [/chatgpt|openai/i, 'OpenAI'],
        [/firefly/i, 'Adobe Firefly'], [/stable[\s_-]?diffusion|sdxl|\bsd3\b/i, 'Stable Diffusion'],
        [/comfyui/i, 'ComfyUI'], [/automatic1111|stable-diffusion-webui/i, 'AUTOMATIC1111'],
        [/novelai/i, 'NovelAI'], [/invokeai/i, 'InvokeAI'], [/fooocus/i, 'Fooocus'],
        [/leonardo\.?ai/i, 'Leonardo.Ai'], [/ideogram/i, 'Ideogram'], [/\bflux(\.1)?\b/i, 'FLUX'],
        [/imagen|gemini|made with google ai/i, 'Google AI'], [/meta ai|imagined with ai/i, 'Meta AI'],
        [/bing image creator|microsoft designer|copilot/i, 'Microsoft'], [/\bsuno\b/i, 'Suno'],
        [/\budio\b/i, 'Udio'], [/eleven[\s_-]?labs/i, 'ElevenLabs'], [/\bsora\b/i, 'Sora'],
        [/\brunway\b/i, 'Runway'], [/\bpika\b/i, 'Pika'], [/\bkling\b/i, 'Kling'], [/\bveo\b/i, 'Google Veo'],
        [/hailuo|minimax/i, 'MiniMax'], [/stable[\s_-]?audio/i, 'Stable Audio'], [/musicgen|audiocraft/i, 'Meta MusicGen'],
        [/\bgrok\b|aurora/i, 'xAI'], [/kandinsky/i, 'Kandinsky'], [/shedevrum|шедеврум/i, 'Шедеврум'],
        [/gigachat|гигачат/i, 'GigaChat']
    ];

    var enc = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
    var dec = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8') : null;
    var latin1 = null;
    try { latin1 = new TextDecoder('latin1'); } catch (e) { latin1 = null; }

    function utf8(u8) { return dec ? dec.decode(u8) : ascii(u8); }
    function ascii(u8) { if (latin1) return latin1.decode(u8); var s = ''; for (var i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return s; }
    function fourcc(u8, o) { return String.fromCharCode(u8[o], u8[o + 1], u8[o + 2], u8[o + 3]); }
    function u32(u8, o) { return ((u8[o] << 24) >>> 0) + (u8[o + 1] << 16) + (u8[o + 2] << 8) + u8[o + 3]; }
    function u32le(u8, o) { return (u8[o] | (u8[o + 1] << 8) | (u8[o + 2] << 16) | (u8[o + 3] << 24)) >>> 0; }
    function hex(u8, a, b) { var s = ''; for (var i = a; i < b; i++) s += (u8[i] < 16 ? '0' : '') + u8[i].toString(16); return s; }
    function clip(s, n) { s = String(s == null ? '' : s).replace(/\u0000+$/g, '').trim(); return s.length > (n || 600) ? s.slice(0, n || 600) + '…' : s; }

    function bytesOf(blob, a, b) {
        return blob.slice(a, b).arrayBuffer().then(function (x) { return new Uint8Array(x); });
    }

    function has(u8, needle, from, to) {
        var pat = []; for (var i = 0; i < needle.length; i++) pat.push(needle.charCodeAt(i));
        var end = Math.min(to == null ? u8.length : to, u8.length) - pat.length;
        outer: for (var p = from || 0; p <= end; p++) {
            for (var j = 0; j < pat.length; j++) if (u8[p + j] !== pat[j]) continue outer;
            return true;
        }
        return false;
    }

    // The description box of a C2PA manifest store: 'jumd' + the type UUID 'c2pa'-0011-0010-8000-00AA00389B71.
    // The letters «c2pa» alone are not a manifest — an XMP namespace or a filename carries them too.
    var C2PA_JUMD = [0x6A, 0x75, 0x6D, 0x64, 0x63, 0x32, 0x70, 0x61, 0x00, 0x11, 0x00, 0x10, 0x80, 0x00, 0x00, 0xAA, 0x00, 0x38, 0x9B, 0x71];
    function c2paStore(u8, from, to) {
        var end = Math.min(to == null ? u8.length : to, u8.length) - C2PA_JUMD.length;
        outer: for (var p = from || 0; p <= end; p++) {
            if (u8[p] !== 0x6A) continue;
            for (var j = 1; j < C2PA_JUMD.length; j++) if (u8[p + j] !== C2PA_JUMD[j]) continue outer;
            return true;
        }
        return false;
    }

    function inflate(u8) {
        if (typeof DecompressionStream === 'undefined') return Promise.resolve(null);
        try {
            var ds = new DecompressionStream('deflate');
            var w = ds.writable.getWriter(); w.write(u8).catch(function () {}); w.close().catch(function () {});
            return new Response(ds.readable).arrayBuffer().then(function (b) { return new Uint8Array(b); }, function () { return null; });
        } catch (e) { return Promise.resolve(null); }
    }

    /* ─────────────────────────── XMP ─────────────────────────── */

    function xmlDecode(s) {
        return String(s).replace(/&(lt|gt|amp|quot|apos|#\d+|#x[0-9a-f]+);/gi, function (m, e) {
            var map = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };
            if (map[e.toLowerCase()]) return map[e.toLowerCase()];
            var n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
            return isFinite(n) ? String.fromCodePoint(n) : m;
        });
    }

    // Values of property `local` in namespace `ns`, element and attribute forms, every prefix
    // the packet binds to it. Containers (rdf:Alt/Seq/Bag) give their rdf:li values.
    function xmpValues(packet, ns, local) {
        var out = [], re = new RegExp('xmlns:([A-Za-z_][\\w.-]*)\\s*=\\s*["\']' + ns.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&') + '["\']', 'g'), m, prefixes = [];
        while ((m = re.exec(packet))) if (prefixes.indexOf(m[1]) < 0) prefixes.push(m[1]);
        prefixes.forEach(function (p) {
            var tag = p + ':' + local;
            var el = new RegExp('<' + tag + '(\\s[^>]*)?>([\\s\\S]*?)</' + tag + '\\s*>', 'g'), e;
            while ((e = el.exec(packet))) {
                var inner = e[2];
                var lis = inner.match(/<rdf:li\b[^>]*>([\s\S]*?)<\/rdf:li>/g);
                if (lis) lis.forEach(function (li) { out.push(xmlDecode(li.replace(/^<rdf:li\b[^>]*>|<\/rdf:li>$/g, '').trim())); });
                else if (inner.trim()) out.push(xmlDecode(inner.replace(/<[^>]+>/g, '').trim()));
            }
            var res = new RegExp('<' + tag + '\\b[^>]*rdf:resource\\s*=\\s*["\']([^"\']*)["\'][^>]*/>', 'g');
            while ((e = res.exec(packet))) out.push(xmlDecode(e[1]));
            var at = new RegExp('\\s' + tag + '\\s*=\\s*("([^"]*)"|\'([^\']*)\')', 'g');
            while ((e = at.exec(packet))) out.push(xmlDecode(e[2] != null ? e[2] : e[3]));
        });
        return out.filter(function (v) { return v !== ''; });
    }

    function readXmp(packet) {
        var p = {};
        var put = function (key, ns, local) { var v = xmpValues(packet, ns, local); if (v.length) p[key] = v; };
        put('DigitalSourceType', NS.iptcExt, 'DigitalSourceType');
        put('AISystemUsed', NS.iptcExt, 'AISystemUsed');
        put('AISystemVersionUsed', NS.iptcExt, 'AISystemVersionUsed');
        put('AIPromptInformation', NS.iptcExt, 'AIPromptInformation');
        put('AIPromptWriterName', NS.iptcExt, 'AIPromptWriterName');
        put('AIGC', NS.tc260, 'AIGC');
        put('TimbricaDeclared', NS.tmbai, 'Declared');
        put('CreatorTool', NS.xmp, 'CreatorTool');
        put('Description', NS.dc, 'description');
        put('Creator', NS.dc, 'creator');
        put('Credit', NS.photoshop, 'Credit');
        put('Make', NS.tiff, 'Make');
        put('Model', NS.tiff, 'Model');
        put('DataMining', NS.plus, 'DataMining');
        put('SoftwareAgent', 'http://ns.adobe.com/xap/1.0/sType/ResourceEvent#', 'softwareAgent');
        put('Provenance', NS.dcterms, 'provenance');
        return p;
    }

    /* ─────────────────────────── EXIF (TIFF) ─────────────────────────── */

    var EXIF_TAGS = { 0x010E: 'ImageDescription', 0x010F: 'Make', 0x0110: 'Model', 0x0131: 'Software', 0x013B: 'Artist',
                      0x8298: 'Copyright', 0x9003: 'DateTimeOriginal', 0x9286: 'UserComment', 0x927C: 'MakerNote',
                      0xA434: 'LensModel', 0xA433: 'LensMake', 0xA430: 'CameraOwnerName', 0xA431: 'BodySerialNumber' };

    function readTiff(u8, base) {
        var out = {};
        if (base + 8 > u8.length) return out;
        var le = u8[base] === 0x49 && u8[base + 1] === 0x49;
        if (!le && !(u8[base] === 0x4D && u8[base + 1] === 0x4D)) return out;
        var g16 = function (o) { return le ? u8[o] | (u8[o + 1] << 8) : (u8[o] << 8) | u8[o + 1]; };
        var g32 = function (o) { return le ? u32le(u8, o) : u32(u8, o); };
        var seen = {};
        function ifd(off, depth) {
            var at = base + off;
            if (depth > 3 || seen[at] || at + 2 > u8.length) return;
            seen[at] = 1;
            var n = g16(at);
            if (n > 1000) return;
            for (var i = 0; i < n; i++) {
                var e = at + 2 + i * 12;
                if (e + 12 > u8.length) return;
                var tag = g16(e), type = g16(e + 2), count = g32(e + 4);
                if (tag === 0x8769 || tag === 0xA005) { ifd(g32(e + 8), depth + 1); continue; }
                var name = EXIF_TAGS[tag];
                if (!name) continue;
                if (tag === 0x927C) { out.MakerNote = count; continue; }
                var size = count * ({ 1: 1, 2: 1, 7: 1, 3: 2, 4: 4 }[type] || 1);
                var vo = size <= 4 ? e + 8 : base + g32(e + 8);
                if (vo + size > u8.length || size > 65536) continue;
                var raw = u8.subarray(vo, vo + size), v;
                if (tag === 0x9286) {                                         // UserComment: 8-byte charset id
                    var cs = ascii(raw.subarray(0, 8)).replace(/\0/g, '').trim();
                    var body = raw.subarray(8);
                    if (/^UNICODE/i.test(cs)) {
                        // The byte order of UTF-16 here is the writer's, not always the TIFF's
                        // (piexif, which AUTOMATIC1111 uses, writes big-endian): Latin text has
                        // its zero bytes on the even positions in BE, on the odd ones in LE.
                        var ze = 0, zo = 0;
                        for (var k = 0; k + 1 < body.length && k < 400; k += 2) { if (!body[k]) ze++; if (!body[k + 1]) zo++; }
                        var bigEnd = ze >= zo, s = '';
                        for (k = 0; k + 1 < body.length; k += 2) {
                            var c = bigEnd ? (body[k] << 8) | body[k + 1] : body[k] | (body[k + 1] << 8);
                            if (!c) break;
                            s += String.fromCharCode(c);
                        }
                        v = s;
                    } else v = utf8(body);
                } else v = utf8(raw);
                v = clip(v, 2000);
                if (v) out[name] = v;
            }
        }
        ifd(g32(base + 4), 0);
        return out;
    }

    /* ─────────────────────────── report helpers ─────────────────────────── */

    function blank(format, size) {
        return { format: format, size: size, c2pa: { present: false, where: null }, xmp: [], exif: {}, texts: [], items: [], notes: [] };
    }

    function addXmp(r, where, packet) {
        if (!packet) return;
        r.xmp.push({ where: where, props: readXmp(packet) });
    }

    function addText(r, where, key, value) {
        if (value == null) return;
        value = clip(value, 4000);
        if (!value && value !== '0') return;
        r.texts.push({ where: where, key: key, value: value });
    }

    /* ─────────────────────────── containers ─────────────────────────── */

    function readJpeg(u8, r) {
        var p = 2, xmpCount = 0;
        while (p + 4 <= u8.length) {
            if (u8[p] !== 0xFF) break;
            var m = u8[p + 1];
            if (m === 0xFF) { p++; continue; }
            if (m === 0xDA || m === 0xD9) break;
            if (m >= 0xD0 && m <= 0xD7) { p += 2; continue; }
            var len = (u8[p + 2] << 8) | u8[p + 3];
            if (len < 2 || p + 2 + len > u8.length) break;
            var d = p + 4, e = p + 2 + len;
            if (m === 0xE1 && has(u8, 'Exif\0\0', d, d + 6)) {
                var ex = readTiff(u8, d + 6);
                Object.keys(ex).forEach(function (k) { if (r.exif[k] == null) r.exif[k] = ex[k]; });
            } else if (m === 0xE1 && has(u8, 'http://ns.adobe.com/xap/1.0/\0', d, d + 29)) {
                xmpCount++;
                addXmp(r, 'XMP (JPEG APP1)', utf8(u8.subarray(d + 29, e)));
            } else if (m === 0xEB && u8[d] === 0x4A && u8[d + 1] === 0x50) {            // APP11 'JP': JUMBF
                if (c2paStore(u8, d, e) || has(u8, 'c2pa', d, e)) { r.c2pa.present = true; r.c2pa.where = 'JPEG APP11 (JUMBF)'; }
            } else if (m === 0xFE) {
                addText(r, 'JPEG COM', 'Comment', utf8(u8.subarray(d, e)));
            }
            p = e;
        }
        if (xmpCount > 1) r.notes.push('xmp_twice');
    }

    function readPng(u8, r) {
        var p = 8, jobs = [];
        while (p + 12 <= u8.length) {
            var len = u32(u8, p), type = fourcc(u8, p + 4), d = p + 8;
            if (d + len > u8.length) break;
            var data = u8.subarray(d, d + len);
            if (type === 'tEXt') {
                var z = data.indexOf(0);
                if (z > 0) addText(r, 'PNG tEXt', ascii(data.subarray(0, z)), ascii(data.subarray(z + 1)));
            } else if (type === 'zTXt') {
                (function (data) {
                    var z = data.indexOf(0);
                    if (z > 0) jobs.push(inflate(data.subarray(z + 2)).then(function (t) { if (t) addText(r, 'PNG zTXt', ascii(data.subarray(0, z)), ascii(t)); }));
                })(data);
            } else if (type === 'iTXt') {
                (function (data) {
                    var z = data.indexOf(0); if (z < 0) return;
                    var kw = utf8(data.subarray(0, z)), comp = data[z + 1];
                    var q = data.indexOf(0, z + 3); if (q < 0) return;
                    var s = data.indexOf(0, q + 1); if (s < 0) return;
                    var body = data.subarray(s + 1);
                    var use = function (t) {
                        if (kw === 'XML:com.adobe.xmp') addXmp(r, 'XMP (PNG iTXt)', utf8(t));
                        else addText(r, 'PNG iTXt', kw, utf8(t));
                    };
                    if (comp) jobs.push(inflate(body).then(function (t) { if (t) use(t); })); else use(body);
                })(data);
            } else if (type === 'eXIf') {
                var ex = readTiff(u8, d);
                Object.keys(ex).forEach(function (k) { if (r.exif[k] == null) r.exif[k] = ex[k]; });
            } else if (type === 'caBX') {
                r.c2pa.present = true; r.c2pa.where = 'PNG caBX';
            }
            if (type === 'IEND') break;
            p = d + len + 4;
        }
        return Promise.all(jobs);
    }

    function readWebp(u8, r) {
        var p = 12;
        while (p + 8 <= u8.length) {
            var t = fourcc(u8, p), len = u32le(u8, p + 4), d = p + 8;
            if (d + len > u8.length) break;
            if (t === 'XMP ') addXmp(r, 'XMP (WebP chunk)', utf8(u8.subarray(d, d + len)));
            else if (t === 'EXIF') {
                var off = has(u8, 'Exif\0\0', d, d + 6) ? d + 6 : d;
                var ex = readTiff(u8, off);
                Object.keys(ex).forEach(function (k) { if (r.exif[k] == null) r.exif[k] = ex[k]; });
            } else if (t === 'C2PA') { r.c2pa.present = true; r.c2pa.where = 'WebP C2PA chunk'; }
            p = d + len + (len & 1);
        }
    }

    /* ISO-BMFF: boxes inside [a, b) of u8 */
    function boxes(u8, a, b) {
        var out = [], p = a;
        while (p + 8 <= b) {
            var len = u32(u8, p), type = fourcc(u8, p + 4), head = 8;
            if (len === 1) { if (p + 16 > b) break; len = u32(u8, p + 8) * 4294967296 + u32(u8, p + 12); head = 16; }
            else if (len === 0) len = b - p;
            if (len < head || p + len > b) break;
            out.push({ type: type, at: p, data: p + head, end: p + len });
            p += len;
        }
        return out;
    }

    function find(u8, list, type) { for (var i = 0; i < list.length; i++) if (list[i].type === type) return list[i]; return null; }

    // moov > udta > meta > ilst (iTunes-style), moov > meta (keys + ilst, mdta), udta ©xxx, udta XMP_
    function readMoov(u8, mv, r) {
        var kids = boxes(u8, mv.data, mv.end);
        var udta = find(u8, kids, 'udta');
        if (udta) {
            var uk = boxes(u8, udta.data, udta.end);
            uk.forEach(function (b) {
                if (b.type === 'XMP_') addXmp(r, 'XMP (MOV udta)', utf8(u8.subarray(b.data, b.end)));
                else if (b.type.charCodeAt(0) === 0xA9 && b.type !== '\u00a9xyz') {
                    // QuickTime classic text: u16 len, u16 lang, text
                    var l = (u8[b.data] << 8) | u8[b.data + 1];
                    if (l && b.data + 4 + l <= b.end) addText(r, 'MOV udta', b.type, utf8(u8.subarray(b.data + 4, b.data + 4 + l)));
                } else if (b.type === 'meta') readIlstMeta(u8, b, r, 'MP4 udta/meta/ilst');
            });
        }
        var meta = find(u8, kids, 'meta');
        if (meta) readIlstMeta(u8, meta, r, 'MOV meta (keys)');
    }

    function readIlstMeta(u8, meta, r, where) {
        // `meta` is a FullBox in MP4 (4 bytes version/flags), a plain box in QuickTime.
        var start = meta.data;
        var probe = boxes(u8, meta.data + 4, meta.end);
        if (probe.length && find(u8, probe, 'hdlr')) start = meta.data + 4;
        var kids = boxes(u8, start, meta.end);
        var keys = [], kb = find(u8, kids, 'keys');
        if (kb) {
            var n = u32(u8, kb.data + 4), p = kb.data + 8;
            for (var i = 0; i < n && p + 8 <= kb.end; i++) {
                var ks = u32(u8, p); if (ks < 8 || p + ks > kb.end) break;
                keys.push(utf8(u8.subarray(p + 8, p + ks))); p += ks;
            }
        }
        var ilst = find(u8, kids, 'ilst');
        if (!ilst) return;
        boxes(u8, ilst.data, ilst.end).forEach(function (it) {
            var name = it.type;
            if (keys.length && /^\0\0/.test(name)) { var ki = u32(u8, it.at + 4) - 1; if (keys[ki]) name = keys[ki]; }
            var inner = boxes(u8, it.data, it.end), mean = '', nm = '';
            inner.forEach(function (d) {
                if (d.type === 'mean') mean = utf8(u8.subarray(d.data + 4, d.end));
                if (d.type === 'name') nm = utf8(u8.subarray(d.data + 4, d.end));
                if (d.type === 'data' && d.end - d.data > 8) {
                    var t = u32(u8, d.data) & 0xFFFFFF;
                    if (t === 1 || t === 2 || t === 0) addText(r, where, name === '----' ? (nm || mean || name) : name, t === 2 ? utf16be(u8.subarray(d.data + 8, d.end)) : utf8(u8.subarray(d.data + 8, d.end)));
                }
            });
        });
    }

    function utf16be(u8) { var s = ''; for (var i = 0; i + 1 < u8.length; i += 2) s += String.fromCharCode((u8[i] << 8) | u8[i + 1]); return s; }

    // HEIF/AVIF still image: meta > iinf (Exif / XMP items) + iloc.
    function readHeifMeta(u8, meta, r, fileBlob, jobs) {
        var kids = boxes(u8, meta.data + 4, meta.end);
        var iinf = find(u8, kids, 'iinf'), iloc = find(u8, kids, 'iloc');
        if (!iinf || !iloc) return;
        var v = u8[iinf.data], p = iinf.data + 4, count;
        if (v === 0) { count = (u8[p] << 8) | u8[p + 1]; p += 2; } else { count = u32(u8, p); p += 4; }
        var items = {};
        boxes(u8, p, iinf.end).slice(0, count).forEach(function (e) {
            if (e.type !== 'infe') return;
            var ver = u8[e.data], q = e.data + 4, id;
            if (ver < 2) return;
            if (ver === 2) { id = (u8[q] << 8) | u8[q + 1]; q += 2; } else { id = u32(u8, q); q += 4; }
            q += 2;                                                         // protection index
            var type = fourcc(u8, q); q += 4;
            var z = u8.indexOf(0, q); q = z < 0 ? e.end : z + 1;          // item name
            var ct = '';
            if (type === 'mime') { var z2 = u8.indexOf(0, q); ct = ascii(u8.subarray(q, z2 < 0 ? e.end : z2)); }
            items[id] = { type: type, ct: ct };
        });
        var lv = u8[iloc.data], q2 = iloc.data + 4;
        var os = u8[q2] >> 4, ls = u8[q2] & 15, bs = u8[q2 + 1] >> 4, is = lv >= 1 ? u8[q2 + 1] & 15 : 0;
        q2 += 2;
        var n = lv < 2 ? ((u8[q2] << 8) | u8[q2 + 1]) : u32(u8, q2); q2 += lv < 2 ? 2 : 4;
        var rd = function (sz) {
            var val = 0; for (var k = 0; k < sz; k++) val = val * 256 + u8[q2 + k]; q2 += sz; return val;
        };
        for (var i = 0; i < n && q2 < iloc.end; i++) {
            var id = lv < 2 ? rd(2) : rd(4);
            if (lv >= 1) rd(2);                                           // construction method
            rd(2);                                                         // data reference index
            var base = rd(bs), ec = rd(2), first = null;
            for (var k = 0; k < ec; k++) {
                if (lv >= 1 && is) rd(is);
                var off = rd(os), len = rd(ls);
                if (!first) first = { off: base + off, len: len };
            }
            var it = items[id];
            if (!it || !first || first.len > 4 * 1048576) continue;
            if (it.type === 'Exif' || (it.type === 'mime' && /rdf\+xml|xmp/i.test(it.ct))) {
                (function (it, first) {
                    jobs.push(bytesOf(fileBlob, first.off, first.off + first.len).then(function (b) {
                        if (it.type === 'Exif') {
                            var skip = b.length >= 4 ? u32(b, 0) : 0;            // offset to the TIFF header
                            var ex = readTiff(b, 4 + skip);
                            Object.keys(ex).forEach(function (k2) { if (r.exif[k2] == null) r.exif[k2] = ex[k2]; });
                        } else addXmp(r, 'XMP (HEIF item)', utf8(b));
                    }).catch(function () {}));
                })(it, first);
            }
        }
    }

    function readBmff(blob, r) {
        var size = blob.size, top = [];
        function walk(off) {
            if (off + 8 > size || top.length > 5000) return Promise.resolve();
            return bytesOf(blob, off, Math.min(size, off + 32)).then(function (h) {
                var len = u32(h, 0), head = 8;
                if (len === 1) { len = u32(h, 8) * 4294967296 + u32(h, 12); head = 16; } else if (len === 0) len = size - off;
                if (len < head || off + len > size) return;
                var b = { type: fourcc(h, 4), at: off, len: len, head: head, uuid: fourcc(h, 4) === 'uuid' ? hex(h, head, head + 16) : '' };
                top.push(b);
                return walk(off + len);
            });
        }
        return walk(0).then(function () {
            var jobs = [];
            var ftyp = top[0] && top[0].type === 'ftyp' ? top[0] : null;
            var brandJob = ftyp ? bytesOf(blob, ftyp.at, ftyp.at + Math.min(ftyp.len, 64)).then(function (f) {
                var major = fourcc(f, 8);
                if (/^(heic|heix|hevc|mif1|msf1)$/.test(major)) r.format = 'heif';
                else if (/^(avif|avis)$/.test(major)) r.format = 'avif';
                else if (/^qt  $/.test(major)) r.format = 'mov';
                else if (/^M4A |^M4B /.test(major)) r.format = 'm4a';
                else r.format = 'mp4';
            }) : Promise.resolve();
            top.forEach(function (b) {
                if (b.type === 'uuid' && b.uuid === C2PA_UUID) { r.c2pa.present = true; r.c2pa.where = 'BMFF uuid (C2PA)'; }
                if (b.type === 'uuid' && b.uuid === XMP_UUID && b.len < 8 * 1048576) {
                    jobs.push(bytesOf(blob, b.at + b.head + 16, b.at + b.len).then(function (x) { addXmp(r, 'XMP (MP4 uuid box)', utf8(x)); }));
                }
                if ((b.type === 'moov' || b.type === 'meta') && b.len < 48 * 1048576) {
                    jobs.push(bytesOf(blob, b.at, b.at + b.len).then(function (m) {
                        var self = { type: b.type, at: 0, data: b.head, end: m.length };
                        if (b.type === 'moov') readMoov(m, self, r);
                        else {
                            var inner = [];
                            readHeifMeta(m, self, r, blob, inner);
                            return Promise.all(inner);
                        }
                    }));
                }
            });
            return brandJob.then(function () { return Promise.all(jobs); });
        });
    }

    function readId3(t, ver, r) {
        var p = 0;
        while (p + 10 <= t.length && t[p] !== 0) {
            var id = fourcc(t, p);
            if (!/^[A-Z0-9]{4}$/.test(id)) break;
            var fs = ver === 4 ? ((t[p + 4] & 0x7F) << 21) | ((t[p + 5] & 0x7F) << 14) | ((t[p + 6] & 0x7F) << 7) | (t[p + 7] & 0x7F) : u32(t, p + 4);
            if (p + 10 + fs > t.length) break;
            var b = t.subarray(p + 10, p + 10 + fs);
            if (id === 'TXXX' || id === 'COMM' || id === 'USLT') {
                var parts = id3Strings(b, id === 'TXXX' ? 1 : 4);
                if (id === 'TXXX') addText(r, 'ID3v2 TXXX', parts[0] || 'TXXX', parts[1] || '');
                else addText(r, 'ID3v2 ' + id, parts[0] ? id + ' (' + parts[0] + ')' : id, parts[1] || '');
            } else if (id.charAt(0) === 'T') {
                if (/^(TSSE|TENC|TIT2|TPE1|TCOP|TPUB|TOWN|TCOM|TIT3|TALB)$/.test(id)) addText(r, 'ID3v2 ' + id, id, id3Strings(b, 1)[0]);
            } else if (id === 'PRIV') {
                var z = b.indexOf(0);
                if (z === 3 && b[0] === 0x58 && b[1] === 0x4D && b[2] === 0x50) addXmp(r, 'XMP (ID3v2 PRIV)', utf8(b.subarray(4)));
            } else if (id === 'GEOB' && c2paStore(b)) { r.c2pa.present = true; r.c2pa.where = 'ID3v2 GEOB (C2PA)'; }
            p += 10 + fs;
        }
    }

    // ID3 text with encodings 0–3: the strings separated by the terminator (COMM skips its language).
    function id3Strings(b, skip) {
        var e = b[0], body = b.subarray(skip), out = [], cur = '';
        if (e === 0 || e === 3) {
            var s = e === 0 ? ascii(body) : utf8(body);
            return s.split('\u0000');
        }
        var be = false, i = 0;
        for (; i + 1 < body.length; i += 2) {
            if (body[i] === 0xFF && body[i + 1] === 0xFE) { be = false; continue; }
            if (body[i] === 0xFE && body[i + 1] === 0xFF) { be = true; continue; }
            var c = e === 2 || be ? (body[i] << 8) | body[i + 1] : body[i] | (body[i + 1] << 8);
            if (!c) { out.push(cur); cur = ''; continue; }
            cur += String.fromCharCode(c);
        }
        out.push(cur);
        return out;
    }

    function readRiffWave(blob, r) {
        var size = blob.size, jobs = [], n = 0;
        function walk(off) {
            if (off + 8 > size || n++ > 2000) return Promise.resolve();
            return bytesOf(blob, off, off + 8).then(function (c) {
                var t = fourcc(c, 0), len = u32le(c, 4), d = off + 8;
                if (!/^[\x20-\x7e]{4}$/.test(t) || d + len > size) return;
                if (t === 'LIST' && len < 1048576) jobs.push(bytesOf(blob, d, d + len).then(function (l) {
                    if (fourcc(l, 0) !== 'INFO') return;
                    for (var q = 4; q + 8 <= l.length;) {
                        var id = fourcc(l, q), sz = u32le(l, q + 4);
                        addText(r, 'WAV LIST/INFO', id, utf8(l.subarray(q + 8, q + 8 + sz)));
                        q += 8 + sz + (sz & 1);
                    }
                }));
                else if (t === '_PMX' && len < 8 * 1048576) jobs.push(bytesOf(blob, d, d + len).then(function (x) { addXmp(r, 'XMP (WAV _PMX chunk)', utf8(x)); }));
                else if (t === 'bext' && len < 1048576) jobs.push(bytesOf(blob, d, d + len).then(function (x) {
                    addText(r, 'WAV bext', 'Description', ascii(x.subarray(0, 256)));
                    addText(r, 'WAV bext', 'Originator', ascii(x.subarray(256, 288)));
                }));
                else if (t === 'C2PA') { r.c2pa.present = true; r.c2pa.where = 'WAV C2PA chunk'; }
                return walk(d + len + (len & 1));
            });
        }
        return walk(12).then(function () { return Promise.all(jobs); });
    }

    // Matroska: the SimpleTags of every Tags element in the head (and at the end, where older
    // writers — ours until 02.10.2026 — put them), plus Info's MuxingApp/WritingApp.
    function readEbmlTags(u8, r, where) {
        function vint(p, keepMarker) {
            var f = u8[p], w = 0;
            for (var k = 0; k < 8; k++) if (f & (0x80 >> k)) { w = k + 1; break; }
            if (!w || p + w > u8.length) return null;
            var v = keepMarker ? f : f & ((0x80 >> (w - 1)) - 1), ones = !keepMarker && v === ((0x80 >> (w - 1)) - 1);
            for (k = 1; k < w; k++) { v = v * 256 + u8[p + k]; if (u8[p + k] !== 0xFF) ones = false; }
            return { v: ones ? -1 : v, w: w };
        }
        function walk(a, b, depth, ctx) {
            var p = a;
            while (p < b && depth < 8) {
                var id = vint(p, true); if (!id) return;
                var sz = vint(p + id.w, false); if (!sz) return;
                var d = p + id.w + sz.w, e = sz.v < 0 ? b : Math.min(b, d + sz.v);
                var I = id.v;
                if (I === 0x18538067 || I === 0x1254C367 || I === 0x7373 || I === 0x1549A966) walk(d, e, depth + 1, ctx);
                else if (I === 0x67C8) { var t = { name: '', value: '' }; walk(d, e, depth + 1, t); if (t.name) addText(r, where, t.name, t.value); }
                else if (I === 0x45A3) ctx.name = utf8(u8.subarray(d, e));
                else if (I === 0x4487) ctx.value = utf8(u8.subarray(d, e));
                else if (I === 0x4D80) addText(r, where, 'MuxingApp', utf8(u8.subarray(d, e)));
                else if (I === 0x5741) addText(r, where, 'WritingApp', utf8(u8.subarray(d, e)));
                else if (I === 0x1F43B675) return;                         // a Cluster: the head is over
                if (sz.v < 0) return;
                p = e;
            }
        }
        walk(0, u8.length, 0, {});
    }

    function readOggComments(u8, r) {
        // The comment packet: 'OpusTags' or 0x03 'vorbis', anywhere in the first pages.
        var at = -1, pre = 0;
        for (var i = 0; i + 8 < u8.length; i++) {
            if (u8[i] === 0x4F && fourcc(u8, i) === 'Opus' && fourcc(u8, i + 4) === 'Tags') { at = i; pre = 8; break; }
            if (u8[i] === 0x03 && ascii(u8.subarray(i + 1, i + 7)) === 'vorbis' && i > 40) { at = i; pre = 7; break; }
        }
        if (at < 0) return;
        var p = at + pre, vl = u32le(u8, p); p += 4 + vl;
        if (p + 4 > u8.length) return;
        addText(r, 'Ogg comments', 'vendor', utf8(u8.subarray(at + pre + 4, at + pre + 4 + vl)));
        var n = u32le(u8, p); p += 4;
        for (var k = 0; k < n && k < 200 && p + 4 <= u8.length; k++) {
            var l = u32le(u8, p); p += 4;
            if (p + l > u8.length) break;
            var s = utf8(u8.subarray(p, p + l)), eq = s.indexOf('=');
            if (eq > 0) addText(r, 'Ogg comments', s.slice(0, eq), s.slice(eq + 1));
            p += l;
        }
    }

    function readFlac(u8, r) {
        var p = 4;
        while (p + 4 <= u8.length) {
            var last = u8[p] & 0x80, type = u8[p] & 0x7F, len = (u8[p + 1] << 16) | (u8[p + 2] << 8) | u8[p + 3];
            var d = p + 4;
            if (d + len > u8.length) break;
            if (type === 4) {
                var q = d, vl = u32le(u8, q); q += 4 + vl;
                var n = u32le(u8, q); q += 4;
                for (var k = 0; k < n && k < 200 && q + 4 <= d + len; k++) {
                    var l = u32le(u8, q); q += 4;
                    var s = utf8(u8.subarray(q, q + l)), eq = s.indexOf('=');
                    if (eq > 0) addText(r, 'FLAC Vorbis comment', s.slice(0, eq), s.slice(eq + 1));
                    q += l;
                }
            }
            if (last) break;
            p = d + len;
        }
    }

    /* ─────────────────────────── the walk ─────────────────────────── */

    var HEAD = 4 * 1048576, TAIL = 1048576, IMAGE_MAX = 80 * 1048576;

    function sniff(h) {
        if (h[0] === 0xFF && h[1] === 0xD8) return 'jpeg';
        if (h[0] === 0x89 && h[1] === 0x50 && h[2] === 0x4E && h[3] === 0x47) return 'png';
        if (fourcc(h, 0) === 'RIFF' && fourcc(h, 8) === 'WEBP') return 'webp';
        if ((fourcc(h, 0) === 'RIFF' || fourcc(h, 0) === 'RF64') && fourcc(h, 8) === 'WAVE') return 'wav';
        if (fourcc(h, 4) === 'ftyp') return 'bmff';
        if (h[0] === 0x1A && h[1] === 0x45 && h[2] === 0xDF && h[3] === 0xA3) return 'webm';
        if (fourcc(h, 0) === 'OggS') return 'ogg';
        if (fourcc(h, 0) === 'fLaC') return 'flac';
        if (fourcc(h, 0) === 'GIF8') return 'gif';
        if ((h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33) || (h[0] === 0xFF && (h[1] & 0xE0) === 0xE0)) return 'mp3';
        return 'unknown';
    }

    function inspect(blob, name) {
        var size = blob.size;
        return bytesOf(blob, 0, Math.min(size, 16)).then(function (h) {
            var kind = sniff(h), r = blank(kind === 'bmff' ? 'mp4' : kind, size);
            var p;
            if (kind === 'jpeg' || kind === 'png' || kind === 'webp' || kind === 'gif') {
                p = bytesOf(blob, 0, Math.min(size, IMAGE_MAX)).then(function (u8) {
                    if (kind === 'jpeg') readJpeg(u8, r);
                    else if (kind === 'png') return readPng(u8, r);
                    else if (kind === 'webp') readWebp(u8, r);
                    else if (kind === 'gif' && has(u8, 'XMP DataXMP')) r.notes.push('gif_xmp');
                });
            } else if (kind === 'bmff') p = readBmff(blob, r);
            else if (kind === 'wav') p = readRiffWave(blob, r);
            else if (kind === 'mp3') {
                p = bytesOf(blob, 0, Math.min(size, 10)).then(function (t) {
                    if (!(t[0] === 0x49 && t[1] === 0x44 && t[2] === 0x33)) return;
                    var ts = ((t[6] & 0x7F) << 21) | ((t[7] & 0x7F) << 14) | ((t[8] & 0x7F) << 7) | (t[9] & 0x7F);
                    if (ts > 64 * 1048576) return;
                    return bytesOf(blob, 10, 10 + ts).then(function (tag) { if (t[3] === 3 || t[3] === 4) readId3(tag, t[3], r); });
                });
            } else if (kind === 'webm') {
                p = Promise.all([bytesOf(blob, 0, Math.min(size, HEAD)), bytesOf(blob, Math.max(0, size - TAIL), size)]).then(function (x) {
                    readEbmlTags(x[0], r, 'Matroska Tags');
                    // Tags appended at the end of the file: walk from the first Tags ID found in the tail.
                    var t = x[1];
                    for (var i = 0; i + 4 < t.length; i++) {
                        if (t[i] === 0x12 && t[i + 1] === 0x54 && t[i + 2] === 0xC3 && t[i + 3] === 0x67 && size - TAIL + i > HEAD) { readEbmlTags(t.subarray(i), r, 'Matroska Tags (end)'); break; }
                    }
                });
            } else if (kind === 'ogg') p = bytesOf(blob, 0, Math.min(size, 262144)).then(function (u8) { readOggComments(u8, r); });
            else if (kind === 'flac') p = bytesOf(blob, 0, Math.min(size, HEAD)).then(function (u8) { readFlac(u8, r); });
            else p = Promise.resolve();
            return p.then(function () {
                // C2PA lives in container-specific places above; a manifest in a place this walk
                // does not know (a new container) still has its JUMBF labels — look once.
                if (r.c2pa.present || kind === 'unknown') return;
                return Promise.all([bytesOf(blob, 0, Math.min(size, HEAD)), bytesOf(blob, Math.max(0, size - TAIL), size)]).then(function (x) {
                    if (c2paStore(x[0]) || c2paStore(x[1])) { r.c2pa.present = true; r.c2pa.where = 'JUMBF'; }
                });
            }).then(function () { normalise(r); return r; });
        });
    }

    /* ─────────────────────────── normalise into claims ─────────────────────────── */

    function dstName(v) {
        var s = String(v || '').trim();
        if (s.indexOf(DST_URI) === 0) s = s.slice(DST_URI.length);
        return s.replace(/^.*\//, '');
    }

    function aiApp(text) {
        for (var i = 0; i < AI_APPS.length; i++) if (AI_APPS[i][0].test(text)) return AI_APPS[i][1];
        return null;
    }

    function parseAigc(raw) {
        var s = String(raw || '').trim();
        try {
            var j = JSON.parse(s);
            if (j && j.AIGC && typeof j.AIGC === 'object') j = j.AIGC;
            if (j && typeof j === 'object') return j;
        } catch (e) { /* not JSON */ }
        return null;
    }

    var GENERATOR_KEYS = {
        parameters: 'AUTOMATIC1111 / Forge', prompt: 'ComfyUI', workflow: 'ComfyUI', invokeai_metadata: 'InvokeAI',
        'sd-metadata': 'InvokeAI', Dream: 'InvokeAI', fooocus_scheme: 'Fooocus', 'generation_data': 'Stable Diffusion'
    };

    function normalise(r) {
        var items = r.items, seen = {};
        var push = function (it) {
            var k = it.kind + '|' + it.code + '|' + it.value;
            if (seen[k]) return; seen[k] = 1; items.push(it);
        };
        r.xmp.forEach(function (x) {
            var P = x.props;
            (P.DigitalSourceType || []).forEach(function (v) {
                var n = dstName(v), kind = DST_KIND[n] || 'info';
                push({ kind: kind, source: x.where, key: 'Iptc4xmpExt:DigitalSourceType', value: n, code: 'dst_' + n });
            });
            (P.AISystemUsed || []).forEach(function (v) { push({ kind: 'ai_maybe', source: x.where, key: 'Iptc4xmpExt:AISystemUsed', value: v, code: 'ai_system' }); });
            (P.AIPromptInformation || []).forEach(function (v) { push({ kind: 'ai_maybe', source: x.where, key: 'Iptc4xmpExt:AIPromptInformation', value: v, code: 'ai_prompt' }); });
            (P.AIGC || []).forEach(function (v) { pushAigc(push, x.where, v); });
            (P.TimbricaDeclared || []).forEach(function (v) { push({ kind: 'info', source: x.where, key: 'tmbai:Declared', value: v, code: 'timbrica_declared' }); });
            // A C2PA manifest kept OUTSIDE the file: XMP dcterms:provenance names its address
            // (C2PA 2.x, 11.2 «remote manifests»). We do not fetch it — a file must not make the
            // visitor's browser call an address it names — but «no marks» would be untrue.
            (P.Provenance || []).forEach(function (v) {
                if (/^https?:\/\//i.test(v)) push({ kind: 'info', source: x.where, key: 'dcterms:provenance', value: v, code: 'c2pa_remote' });
            });
            ['CreatorTool', 'SoftwareAgent', 'Credit'].forEach(function (k) {
                (P[k] || []).forEach(function (v) {
                    var app = aiApp(v);
                    if (app) push({ kind: 'ai_maybe', source: x.where, key: k, value: v, code: 'ai_app', app: app });
                });
            });
            (P.Description || []).forEach(function (v) {
                if (v.indexOf(OUR_NOTE) === 0) push({ kind: 'ai', source: x.where, key: 'dc:description', value: v, code: 'timbrica_generator', tool: ourTool(v) });
                else if (/job id:\s*[0-9a-f-]{8,}/i.test(v)) push({ kind: 'ai', source: x.where, key: 'dc:description', value: v, code: 'midjourney_prompt', app: 'Midjourney' });
            });
            if ((P.Make || []).length) push({ kind: 'capture', source: x.where, key: 'tiff:Make', value: P.Make[0] + (P.Model ? ' ' + P.Model[0] : ''), code: 'camera_xmp' });
            (P.DataMining || []).forEach(function (v) { push({ kind: 'info', source: x.where, key: 'plus:DataMining', value: dstName(v), code: 'data_mining' }); });
        });
        var ex = r.exif;
        if (ex.Make || ex.Model) {
            // «Canon» + «Canon EOS R6» is one name, not two
            var cam = ex.Make && ex.Model && ex.Model.toLowerCase().indexOf(ex.Make.toLowerCase()) === 0 ? ex.Model : [ex.Make, ex.Model].filter(Boolean).join(' ');
            push({ kind: 'capture', source: 'EXIF', key: 'Make / Model', value: cam, code: ex.MakerNote ? 'camera_makernote' : 'camera_exif' });
        }
        ['Software', 'Artist', 'ImageDescription'].forEach(function (k) {
            if (!ex[k]) return;
            var app = aiApp(ex[k]);
            if (app) push({ kind: 'ai_maybe', source: 'EXIF', key: k, value: ex[k], code: 'ai_app', app: app });
        });
        if (ex.UserComment) {
            if (/steps:\s*\d+.*sampler:/i.test(ex.UserComment.replace(/\n/g, ' '))) push({ kind: 'ai', source: 'EXIF', key: 'UserComment', value: ex.UserComment, code: 'sd_parameters', app: 'AUTOMATIC1111 / Forge' });
            else if (aiApp(ex.UserComment)) push({ kind: 'ai_maybe', source: 'EXIF', key: 'UserComment', value: ex.UserComment, code: 'ai_app', app: aiApp(ex.UserComment) });
        }
        r.texts.forEach(function (t) {
            var key = t.key, v = t.value;
            if (key === 'AIGC') { pushAigc(push, t.where, v); return; }
            // WebM/MKV have no XMP: /ai-label's own declaration rides in the AI_LABEL tag
            if (/^AI_LABEL$/.test(key) && /Timbrica ai-label/.test(v)) { push({ kind: 'info', source: t.where, key: key, value: v, code: 'timbrica_declared' }); return; }
            if (v.indexOf(OUR_NOTE) === 0) { push({ kind: 'ai', source: t.where, key: key, value: v, code: 'timbrica_generator', tool: ourTool(v) }); return; }
            if (/^(digitalsourcetype|DIGITAL_SOURCE_TYPE)$/i.test(key)) {
                var n = dstName(v); push({ kind: DST_KIND[n] || 'info', source: t.where, key: key, value: n, code: 'dst_' + n }); return;
            }
            if (/^AI_GENERATED$/i.test(key) && /^(true|1|yes)$/i.test(v)) { push({ kind: 'ai', source: t.where, key: key, value: v, code: 'ai_generated_flag' }); return; }
            if (r.format === 'png' && GENERATOR_KEYS[key] && v.length > 2) {
                var app = GENERATOR_KEYS[key];
                if (key === 'prompt' && !/^\s*\{/.test(v)) app = null;            // ComfyUI's is a JSON graph
                if (app) { push({ kind: 'ai', source: t.where, key: key, value: v, code: key === 'parameters' ? 'sd_parameters' : 'generator_chunk', app: app }); return; }
            }
            if (/^(Software|Source|Comment|Description|Title|Author|ISFT|ICMT|TSSE|TENC|encoder|ENCODER|WritingApp|MuxingApp|©too|©cmt|\u00a9too|\u00a9cmt|com\.apple\.quicktime\.software|vendor)$/i.test(key) || /TXXX|COMM/.test(t.where)) {
                var a = aiApp(v);
                if (a) push({ kind: r.format === 'png' && /NovelAI/.test(a) ? 'ai' : 'ai_maybe', source: t.where, key: key, value: v, code: 'ai_app', app: a });
                else if (/job id:\s*[0-9a-f-]{8,}/i.test(v)) push({ kind: 'ai', source: t.where, key: key, value: v, code: 'midjourney_prompt', app: 'Midjourney' });
            }
            if (/^com\.apple\.quicktime\.(make|model)$/.test(key)) push({ kind: 'capture', source: t.where, key: key, value: v, code: 'camera_quicktime' });
        });
        if (r.c2pa.present) push({ kind: 'info', source: r.c2pa.where, key: 'C2PA', value: '', code: 'c2pa_present' });
    }

    function pushAigc(push, where, raw) {
        var j = parseAigc(raw);
        var lab = j ? String(j.Label != null ? j.Label : '') : '';
        var kind = lab === '1' ? 'ai' : (lab === '2' || lab === '3') ? 'ai_maybe' : 'info';
        push({ kind: kind, source: where, key: 'AIGC', value: raw, code: 'aigc_' + (lab || 'raw'), aigc: j });
    }

    function ourTool(v) { var m = /\(tool:\s*([a-z0-9-]+)/i.exec(v); return m ? m[1] : ''; }

    /* ─────────────────────────── the verdict ─────────────────────────── */
    /* level: 'ai' | 'ai_partial' | 'ai_maybe' | 'capture' | 'none' | 'info'
       contradictions: codes the page explains — two claims in one file that cannot both be true. */
    function verdict(r, c2pa) {
        var items = r.items.slice(0);
        if (c2pa && c2pa.items) items = items.concat(c2pa.items);
        var by = function (k) { return items.filter(function (i) { return i.kind === k; }); };
        var ai = by('ai'), part = by('ai_partial'), maybe = by('ai_maybe'), cap = by('capture'), enh = by('enhanced');
        var contradictions = [];
        var dsts = {};
        items.forEach(function (i) { if (/^dst_/.test(i.code)) dsts[i.value] = 1; });
        var dk = Object.keys(dsts);
        if (dk.length > 1) contradictions.push({ code: 'dst_conflict', values: dk });
        if ((ai.length || part.length) && cap.length) contradictions.push({ code: 'ai_and_camera' });
        var aigc = items.filter(function (i) { return /^aigc_/.test(i.code); });
        if (aigc.length && dsts.digitalCapture) contradictions.push({ code: 'aigc_vs_capture' });
        if (c2pa && c2pa.state === 'Invalid') contradictions.push({ code: 'c2pa_invalid' });
        var level = ai.length ? 'ai' : part.length ? 'ai_partial' : maybe.length ? 'ai_maybe' : enh.length ? 'enhanced' : cap.length ? 'capture' : items.length ? 'info' : 'none';
        var ours = items.filter(function (i) { return i.code === 'timbrica_generator'; });
        var declared = items.filter(function (i) { return i.code === 'timbrica_declared'; });
        var google = items.some(function (i) { return i.app === 'Google AI' || /google/i.test(String(i.issuer || '')); });
        return { level: level, ai: ai, partial: part, maybe: maybe, capture: cap, enhanced: enh, contradictions: contradictions,
                 timbrica: ours.length ? { tool: ours[0].tool } : null, declared: declared.length > 0, google: google, items: items };
    }

    var API = { inspect: inspect, verdict: verdict, readXmp: readXmp, readTiff: readTiff, dstName: dstName, aiApp: aiApp, parseAigc: parseAigc, sniff: sniff };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) root.AiLabelInspect = API;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
